import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { TinyMCEConfig } from '@core/components/wyisiwyg-editor/tinymce.config';
import { subHours } from 'date-fns';
import { MicroText } from 'lingo2-models';
import { OnUiButtonState } from 'onclass-ui';
import { first, takeUntil } from 'rxjs/operators';
import { BaseContentCard, IContentCardState } from '../base-content-card';

export interface IMicroTextCardComponentState extends IContentCardState {
  editorInFocus: boolean;
  saveButton: OnUiButtonState;
  editing: boolean;
  editorLoaded: boolean;
  processing: boolean;
  removed: boolean;
}

@Component({
  selector: 'app-microText-card',
  templateUrl: './microText-card.component.html',
  styleUrls: ['./microText-card.component.scss'],
})
export class MicroTextCardComponent extends BaseContentCard implements OnInit {
  @Input() set item(item: MicroText) {
    this._item = item;
    this.init();
  }
  get item(): MicroText {
    return this._item;
  }
  public state: IMicroTextCardComponentState = {
    ...super.getState(),
    editorInFocus: false,
    saveButton: 'default',
    editing: false,
    editorLoaded: false,
    processing: false,
    removed: false,
  };
  public form: UntypedFormGroup;
  public editor;
  public editorConfig = { ...TinyMCEConfig };
  protected editorResetTimeout;
  protected resetButtonStateTimeout;
  protected resetStateEditingTimeout;
  protected _item: MicroText;
  protected _oldAfter = subHours(new Date(), 24);

  ngOnInit() {
    super.ngOnInit();
  }

  protected init() {
    super.init();
    this.title = '';
    this.sub_title = '';
    this.detectChanges();
  }

  public archive() {
    this.state.processing = true;
    this.detectChanges();

    this.contentService
      .archive(this._item.id)
      .pipe(first())
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.state.processing = false;
          this.state.removed = true;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.state.processing = false;
          this.detectChanges();
        },
      });
  }

  public restore() {
    this.state.processing = true;
    this.detectChanges();

    this.contentService
      .restore(this._item.id)
      .pipe(first())
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.state.processing = false;
          this.state.removed = false;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.state.processing = false;
          this.detectChanges();
        },
      });
  }

  public get isOld(): boolean {
    return this._item.created_at <= this._oldAfter;
  }

  public openEditor() {
    if (!this.isAuthor || this.isOld || this.state.removed) {
      return;
    }

    this.createForm();

    void this.configureEditor().then(() => {
      this.state.editing = true;
      this.detectChanges();
    });
  }

  protected createForm() {
    const _item = this._item;

    this.form = this.fb.group({
      text: [_item.text || ''],
    });
    this.form.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => {
        // чепуха какая-то - не проставляется form.dirty
        this.form.markAsDirty();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });
  }

  protected getValuesFromForm(): Partial<MicroText> {
    // TODO обновить или переиспользовать ранее сохранённый или из localStorage
    const { text } = this.form.value;

    return {
      id: this._item.id,
      type: this._item.type,
      text,
    };
  }

  public get text(): AbstractControl {
    return this.form.get('text');
  }

  protected resetButtonState(buttonState: string, timeout = 1500) {
    this.clearTimeout(this.resetButtonStateTimeout);
    this.resetButtonStateTimeout = this.setTimeout(() => {
      this.state[buttonState] = 'default';
      this.detectChanges();
    }, timeout);
  }

  public get readyForSave(): boolean {
    return this.form.dirty && this.form.valid && this.state.saveButton === 'default';
  }

  private configureEditor(): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.state.editorLoaded) {
        resolve(true);
        return;
      }
      this.state.editorLoaded = true;
      resolve(true);
    });
  }

  public save() {
    if (!this.readyForSave) {
      return;
    }

    const values = this.getValuesFromForm();
    if (!values) {
      return;
    }

    this.state.saveButton = 'progress';
    this.detectChanges();

    this.contentService
      .updateContent(values.id, values)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (item) => {
          // patch values
          this._item.text = (item as MicroText).text;

          this.state.saveButton = 'done';
          this.detectChanges();

          this.resetButtonState('saveButton');
          this.clearTimeout(this.resetStateEditingTimeout);
          this.resetStateEditingTimeout = this.setTimeout(() => {
            this.state.editing = false;
            this.detectChanges();
          }, 1500);
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.state.saveButton = 'fail';
          this.resetButtonState('saveButton');
          this.detectChanges();
        },
      });
  }

  public cancel() {
    this.state.editing = false;
    this.detectChanges();
  }

  protected validateForm() {
    // const values = this.getValuesFromForm();
    // this.warnings = values ? ContentValidators.validate(values as Content) : [];
    // if (this.warnings.length === 0) {
    //   this.state.notReadyPopupOpen = false;
    // }
    // if (!this.autoSaveState.values) {
    //   this.autoSaveState.values = JSON.stringify(values);
    // }
  }
}
